import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions as MUIDialogActions,
  Box,
  Button,
  Typography,
  Stack,
  IconButton,
} from '@mui/material';
import { CreateScreen } from './CreateScreen';
import { DoorEditing } from './DoorEditing';
import { WindowEditing } from './WindowEditing';
import { ElectricityEditing } from './ElectricityEditing';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { RoomPreview } from './RoomPreview';
import { getRoomById } from 'stateMgmt/selectors';
import { useApp } from 'stateMgmt/AppContext';
import { actions } from 'stateMgmt/actions';
import { MainScreen } from './MainScreen';
import { convertPointToRealWorld } from 'konvaHelpers';
export function RoomModal({ roomId, onClose }) {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { building, dispatch } = useApp();
  const room = getRoomById(building, roomId);
  const [pendingRoomData, setPendingRoomData] = useState(room);
  const [modalState, setModalState] = useState({
    selectedElement: null,
    selectedElementType: null,
    selectedElementPosition: null,
    mode: 'main',
  });

  useEffect(() => {
    if (!room) {
      onClose();
    }
  }, [room, onClose]);

  if (!room) {
    console.error('Room not found', roomId);
    return null;
  }

  const handleSaveAll = () => {
    // Apply rotation to walls before saving
    dispatch({ type: actions.UPDATE_ROOM, payload: { room: pendingRoomData } });
    onClose();
  };


  const handleCreateSelection = (updatedRoom, elementType, element) => {
    setPendingRoomData(updatedRoom);
    setModalState((prevState) => ({
      ...prevState,
      selectedElement: element,
      selectedElementType: elementType,
      mode: 'edit',
    }));
  };

  const handleEditSave = (updatedRoom) => {
    setPendingRoomData(updatedRoom);
    setModalState({
      selectedElementPosition: null,
      selectedElement: null,
      selectedElementType: null,
      mode: 'main',
    });
  };

  const handleEditClose = () => {
    setModalState({
      selectedElementPosition: null,
      selectedElement: null,
      selectedElementType: null,
      mode: 'main',
    });
  };

  const handleWallClick = (wall, e) => {
    setModalState({
      selectedElement: wall,
      selectedElementType: 'wall',
      selectedElementPosition: convertPointToRealWorld(e),
      mode: 'create',
    });
  };

  const handleDoorClick = (door, e) => {
    setModalState({
      selectedElement: door,
      selectedElementType: 'door',
      selectedElementPosition: convertPointToRealWorld(e),
      mode: 'edit',
    });
  };

  const handleWindowClick = (window, e) => {
    setModalState({
      selectedElement: window,
      selectedElementType: 'window',
      selectedElementPosition: convertPointToRealWorld(e),
      mode: 'edit',
    });
  };

  const handleElectricityPointClick = (electricityPoint, e) => {
    setModalState({
      selectedElement: electricityPoint,
      selectedElementType: 'electricityPoint',
      selectedElementPosition: convertPointToRealWorld(e),
      mode: 'edit',
    });
  };

  const getScreenToRender = () => {
    switch (modalState?.mode) {
      case 'create':
        return (
          <CreateScreen
            room={pendingRoomData}
            wall={modalState?.selectedElement}
            selectedElementPosition={modalState?.selectedElementPosition}
            onSelect={handleCreateSelection}
          />
        );

      case 'edit':
        switch (modalState?.selectedElementType) {
          case 'door':
            return (
              <DoorEditing
                room={pendingRoomData}
                door={modalState?.selectedElement}
                onClose={handleEditClose}
                onSave={handleEditSave}
                onChange={handleCreateSelection}
              />
            );

          case 'window':
            return (
              <WindowEditing
                room={pendingRoomData}
                window={modalState?.selectedElement}
                onClose={handleEditClose}
                onSave={handleEditSave}
                onChange={handleCreateSelection}
              />
            );

          case 'electricityPoint':
            return (
              <ElectricityEditing
                room={pendingRoomData}
                electricityPoint={modalState?.selectedElement}
                onClose={handleEditClose}
                onSave={handleEditSave}
                onChange={handleCreateSelection}
              />
            );

          default:
            return <MainScreen room={pendingRoomData} onChange={setPendingRoomData} />;
        }

      default:
        return <MainScreen room={pendingRoomData} onChange={setPendingRoomData} />;
    }
  };

  const screenToRender = getScreenToRender();
  return (
    <Dialog
      open={!!room}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">עריכת חדר #{room.number || roomId.slice(-4)}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: 'calc(80vh - 120px)', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', height: '100%' }}>
          {/* Left side - Preview */}
          <Box sx={{ width: '400px', borderRight: 1, borderColor: 'divider', p: 2 }}>
            <Typography variant="h6" gutterBottom>תצוגה מקדימה</Typography>
            <Box sx={{ height: 'calc(100% - 50px)', mt: 2 }}>
              <RoomPreview
                room={pendingRoomData}
                selectedElementId={modalState?.selectedElement?.id}
                onWallClick={handleWallClick}
                onDoorClick={handleDoorClick}
                onWindowClick={handleWindowClick}
                onElectricityPointClick={handleElectricityPointClick}
              />
            </Box>
          </Box>

          {/* Right side - Editing panel */}
          {screenToRender}
        </Box>
      </DialogContent>

      <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteConfirmOpen(true)}
          >
            מחק חדר
          </Button>
          <Stack direction="row" spacing={2}>
            <Button onClick={onClose}>ביטול</Button>
            <Button variant="contained" color="primary" onClick={handleSaveAll}>
              שמור הכל
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>מחיקת חדר</DialogTitle>
        <DialogContent>
          <DialogContentText>
            האם אתה בטוח שברצונך למחוק את החדר #{room?.number || roomId.slice(-4)}?
            פעולה זו לא ניתנת לביטול.
          </DialogContentText>
        </DialogContent>
        <MUIDialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>ביטול</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              //removeRoom(roomId);
              setDeleteConfirmOpen(false);
              onClose();
            }}
          >
            מחק
          </Button>
        </MUIDialogActions>
      </Dialog>
    </Dialog>
  );
}
